"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useStudio } from "@/contexts/studio-context";
import { SectionInterstitial } from "@/components/ui/organisms/section-interstitial";
import type { SectionTipsEvent } from "@/lib/studio/components/section-tips";
import type { AnalysisSection } from "@/lib/music/analyzer-types";

type ShowState = {
  section: AnalysisSection;
  sectionIndex: number;
  totalSections: number;
};

/**
 * Studio-connected section tips. Subscribes to Studio section-tips events
 * and displays the SectionInterstitial overlay when a new section starts.
 *
 * Mirrors the pattern of StudioCountIn.
 */
export function StudioSectionTips({
  sections,
}: {
  sections: AnalysisSection[];
}) {
  const { studio } = useStudio();
  const [visible, setVisible] = useState(false);
  const [showState, setShowState] = useState<ShowState | null>(null);
  const generationRef = useRef(0);
  const [pedalDown, setPedalDown] = useState(false);

  /*
    La pédale forte retient le rideau tant qu'elle est enfoncée.

    Le minuteur de lecture est calculé sur 200 mots/minute, une moyenne qui ne
    vaut pour personne en particulier ; il fallait une commande pour le
    suspendre, et la pédale est la seule que le joueur ait sans quitter le
    clavier. On écoute le piano plutôt qu'une entrée MIDI : c'est le bus où se
    rejoignent la vraie pédale et la barre d'espace du clavier d'ordinateur,
    donc les deux marchent sans rien de plus.

    L'état est lu une fois au branchement : le rideau s'ouvre parfois sur une
    pédale déjà enfoncée, et l'événement, lui, est passé avant.

    Branché seulement rideau visible : le reste du temps, l'abonnement
    posait un état à chaque coup de pédale pour un composant qui rend `null`.
  */
  useEffect(() => {
    if (!studio || !visible) {
      return;
    }

    const piano = studio.getPiano();
    setPedalDown(piano.isSustainActive());

    return piano.on((event) => {
      if (event.type === "sustain_on") {
        setPedalDown(true);
      } else if (event.type === "sustain_off") {
        setPedalDown(false);
      }
    });
  }, [studio, visible]);

  // Keep sections in sync with the backend component
  useEffect(() => {
    studio?.sectionTips.setSections(sections);
  }, [studio, sections]);

  useEffect(() => {
    if (!studio) return;

    return studio.sectionTips.on((event: SectionTipsEvent) => {
      switch (event.type) {
        case "show":
          generationRef.current++;
          setShowState({
            section: event.section,
            sectionIndex: event.sectionIndex,
            totalSections: event.totalSections,
          });
          setVisible(true);
          break;
        case "dismiss":
          setVisible(false);
          break;
      }
    });
  }, [studio]);

  const handleComplete = useCallback(() => {
    studio?.sectionTips.complete();
  }, [studio]);

  if (!visible || !showState) return null;

  const { section, sectionIndex, totalSections } = showState;

  return (
    <SectionInterstitial
      key={generationRef.current}
      sectionIndex={sectionIndex}
      sectionLabel={section.label}
      totalSections={totalSections}
      explain={section.explain ?? ""}
      tip={section.tip ?? ""}
      holdPaused={pedalDown}
      onComplete={handleComplete}
    />
  );
}
